import React, { useCallback, useRef, useState } from 'react';
import { Replica, formatId } from '../../lib/rga';

const SEED = 'Ship it';
const LOG_SIZE = 9;

const fresh = () => {
  const a = new Replica('A');
  const b = new Replica('B');
  a.edit(SEED).forEach((op) => b.apply(op));
  return { a, b, toB: [], toA: [], log: [], seq: 0 };
};

const describe = (op) =>
  op.type === 'ins'
    ? `ins ${JSON.stringify(op.value)} after ${formatId(op.after)}`
    : `del ${formatId(op.target)}`;

/**
 * Two replicas of the same document, joined by a link you can cut.
 * While the link is up, every op is delivered the moment it is made.
 * Cut it, edit both sides, restore it, and the queued ops cross over;
 * the two texts come out identical without any coordination.
 */
export const ConvergeDemo = () => {
  const net = useRef(fresh());
  const [online, setOnline] = useState(true);
  const [, setVersion] = useState(0);
  const rerender = useCallback(() => setVersion((v) => v + 1), []);

  const record = (from, ops, delivered) => {
    const n = net.current;
    ops.forEach((op) => {
      n.seq += 1;
      n.log.unshift({ seq: n.seq, from, op, delivered });
    });
    n.log.length = Math.min(n.log.length, LOG_SIZE);
  };

  const flush = () => {
    const n = net.current;
    n.toB.splice(0).forEach((op) => n.b.apply(op));
    n.toA.splice(0).forEach((op) => n.a.apply(op));
    n.log.forEach((entry) => {
      entry.delivered = true;
    });
  };

  const onEdit = (side) => (e) => {
    const n = net.current;
    const [self, other, queue] = side === 'A' ? [n.a, n.b, n.toB] : [n.b, n.a, n.toA];
    const ops = self.edit(e.target.value);
    if (online) {
      ops.forEach((op) => other.apply(op));
    } else {
      queue.push(...ops);
    }
    record(side, ops, online);
    rerender();
  };

  const toggleLink = () => {
    if (!online) flush();
    setOnline(!online);
  };

  const reset = () => {
    net.current = fresh();
    setOnline(true);
    rerender();
  };

  // Both sides type at the end of the same word while cut off.
  const scriptConflict = () => {
    const n = fresh();
    const a = n.a.edit(`${SEED} today`);
    const b = n.b.edit(`${SEED} now`);
    n.toB.push(...a);
    n.toA.push(...b);
    net.current = n;
    record('A', a, false);
    record('B', b, false);
    setOnline(false);
    rerender();
  };

  const n = net.current;
  const inFlight = n.toA.length + n.toB.length;
  const textA = n.a.text();
  const textB = n.b.text();
  const same = textA === textB && inFlight === 0;

  const replica = (label, rep, queued) => (
    <div className="demo-replica">
      <div className="demo-replica-head">
        <p className="demo-replica-name">replica {label}</p>
        <p className="demo-replica-clock">lamport clock {rep.clock}</p>
      </div>
      <textarea
        className="demo-input"
        value={rep.text()}
        onChange={onEdit(label)}
        rows={3}
        spellCheck={false}
        aria-label={`Replica ${label} text`}
      />
      <p className="demo-queue">
        {queued ? 'outbox holding edits until the link is back' : 'outbox empty'}
      </p>
    </div>
  );

  return (
    <div className="demo">
      <div className="demo-bar">
        <p className={online ? 'demo-link is-up' : 'demo-link is-down'}>
          <span className="demo-led" aria-hidden="true" />
          link {online ? 'up' : 'cut'}
        </p>
        <p className="demo-state" aria-live="polite">
          {same ? 'replicas identical' : 'replicas diverged, edits waiting on the link'}
        </p>
        <div className="demo-controls">
          <button type="button" className="action action-solid" onClick={toggleLink}>
            {online ? 'Cut the link' : 'Restore the link'}
          </button>
          <button type="button" className="action" onClick={scriptConflict}>
            Stage a conflict
          </button>
          <button type="button" className="action" onClick={reset}>
            Reset
          </button>
        </div>
      </div>

      <div className="demo-replicas">
        {replica('A', n.a, n.toB.length)}
        {replica('B', n.b, n.toA.length)}
      </div>

      <table className="demo-log">
        <caption>op log, newest first</caption>
        <thead>
          <tr>
            <th scope="col">From</th>
            <th scope="col">Id</th>
            <th scope="col">Operation</th>
            <th scope="col">Delivery</th>
          </tr>
        </thead>
        <tbody>
          {n.log.length === 0 ? (
            <tr>
              <td colSpan={4} className="demo-log-empty">
                Type in either replica. Ops appear here as they are made.
              </td>
            </tr>
          ) : (
            n.log.map((entry) => (
              <tr key={entry.seq}>
                <td>{entry.from}</td>
                <td>{formatId(entry.op.id)}</td>
                <td>{describe(entry.op)}</td>
                <td className={entry.delivered ? undefined : 'is-pending'}>
                  {entry.delivered ? 'delivered' : 'queued'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ConvergeDemo;
